/**
 * BulkDataEditableTable - Editable table for bulk updating data card values
 *
 * Features:
 * - First column (card info) is read-only
 * - Second column (current value) is read-only
 * - Third column (new value) is editable
 * - Keyboard navigation: Tab, Enter, Arrow keys
 * - Copy/paste support for values
 */
import React, { useState, useRef, useCallback } from 'react';
import { WarningIcon } from '@phosphor-icons/react';
import type { DataValueType, LevelCalculationMode, MetricNodeType, UpdateCadence } from '../api/types';
import type { PeriodGrain } from '../utils/constants';
import { validateDataValueInput, type ValidationError } from '../utils/validation';
import Button from 'src/components/ui/Button';

const CADENCE_LABEL: Record<UpdateCadence, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly'
};

const GRAIN_LABEL: Record<PeriodGrain, string> = {
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter'
};

function cadenceForGrain(grain: PeriodGrain): UpdateCadence {
  if (grain === 'week') return 'weekly';
  if (grain === 'quarter') return 'quarterly';
  return 'monthly';
}

function grainForCadence(cadence: UpdateCadence): PeriodGrain {
  if (cadence === 'weekly') return 'week';
  if (cadence === 'quarterly') return 'quarter';
  return 'month';
}

interface DataCardRow {
  id: string;
  name: string;
  path?: string;
  dataValueType: DataValueType | null;
  levelCalculationMode?: LevelCalculationMode | null;
  currentValue: number | null;
  /** Node type – status cards render Yes/No toggles instead of a numeric input. */
  nodeType?: MetricNodeType;
  updateCadence?: UpdateCadence | null;
}

interface BulkDataEditableTableProps {
  /** Data cards to display */
  dataCards: DataCardRow[];
  /** Current values entered by the user (cardId -> value string) */
  values: Record<string, string>;
  /** Callback when values change */
  onValuesChange: (values: Record<string, string>) => void;
  /** Validation errors (cardId -> errors) */
  validationErrors: Record<string, ValidationError[]>;
  /** Callback when validation errors change */
  onValidationErrorsChange: (errors: Record<string, ValidationError[]>) => void;
  /** View grain — only cards whose cadence matches this grain are editable. */
  selectedGrain: PeriodGrain;
}

export default function BulkDataEditableTable({
  dataCards,
  values,
  onValuesChange,
  validationErrors,
  onValidationErrorsChange,
  selectedGrain
}: BulkDataEditableTableProps) {
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
  const requiredCadence = cadenceForGrain(selectedGrain);

  const isEditable = (card: DataCardRow): boolean => (card.updateCadence ?? 'monthly') === requiredCadence;

  // Get value suffix for display
  const getValueSuffix = (dataValueType: DataValueType | null): string => {
    if (dataValueType === 'percentage') return '%';
    return '';
  };

  // Handle value change with validation
  const handleValueChange = useCallback(
    (
      cardId: string,
      value: string,
      dataValueType: DataValueType | null,
      levelCalculationMode?: LevelCalculationMode | null
    ) => {
      // Update value
      onValuesChange({
        ...values,
        [cardId]: value
      });

      // Validate the input
      if (value.trim()) {
        const errors = validateDataValueInput(value, dataValueType, undefined, levelCalculationMode);
        onValidationErrorsChange({
          ...validationErrors,
          [cardId]: errors
        });
      } else {
        // Clear errors if value is empty
        const newErrors = { ...validationErrors };
        delete newErrors[cardId];
        onValidationErrorsChange(newErrors);
      }
    },
    [values, validationErrors, onValuesChange, onValidationErrorsChange]
  );

  const focusEditableFrom = (startIndex: number, direction: 1 | -1) => {
    for (let i = startIndex + direction; i >= 0 && i < dataCards.length; i += direction) {
      const candidate = dataCards[i];
      if (!isEditable(candidate)) continue;
      const input = inputRefs.current.get(candidate.id);
      if (input) {
        input.focus();
        return;
      }
    }
  };

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number) => {
      switch (e.key) {
        case 'Enter':
        case 'ArrowDown':
          e.preventDefault();
          focusEditableFrom(rowIndex, 1);
          break;

        case 'ArrowUp':
          e.preventDefault();
          focusEditableFrom(rowIndex, -1);
          break;

        case 'Tab':
          break;

        case 'Escape':
          e.currentTarget.blur();
          break;
      }
    },
    [dataCards, requiredCadence]
  );

  // Handle paste - supports pasting multiple values
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, startRowIndex: number) => {
      const pastedText = e.clipboardData.getData('text');

      // Check if pasting multiple values (newline or tab separated)
      const pastedValues = pastedText
        .split(/[\n\t\r]+/)
        .map((v) => v.trim())
        .filter((v) => v !== '');

      if (pastedValues.length <= 1) {
        // Single value paste - let default behavior handle it
        return;
      }

      e.preventDefault();

      // Paste into editable rows only, starting from the current row
      const newValues = { ...values };
      const newErrors = { ...validationErrors };
      let pasteIndex = 0;

      for (let i = startRowIndex; i < dataCards.length && pasteIndex < pastedValues.length; i++) {
        const card = dataCards[i];
        if ((card.updateCadence ?? 'monthly') !== requiredCadence) continue;

        const value = pastedValues[pasteIndex];
        pasteIndex += 1;
        newValues[card.id] = value;

        if (value.trim()) {
          const errors = validateDataValueInput(value, card.dataValueType, undefined, card.levelCalculationMode);
          if (errors.length > 0) {
            newErrors[card.id] = errors;
          } else {
            delete newErrors[card.id];
          }
        } else {
          delete newErrors[card.id];
        }
      }

      onValuesChange(newValues);
      onValidationErrorsChange(newErrors);
    },
    [dataCards, values, validationErrors, onValuesChange, onValidationErrorsChange, requiredCadence]
  );

  if (dataCards.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {/* Helper text */}
      <p className="text-xs text-content-subtlest">
        Use <kbd className="px-1 py-0.5 bg-surface-subtle rounded text-[10px]">↑</kbd>/
        <kbd className="px-1 py-0.5 bg-surface-subtle rounded text-[10px]">↓</kbd> or{' '}
        <kbd className="px-1 py-0.5 bg-surface-subtle rounded text-[10px]">Enter</kbd> to navigate. Paste multiple
        values from spreadsheet.
      </p>

      <div className="border border-border-default rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-subtle border-b border-border-default">
              <th className="text-left px-3 py-2 font-semibold text-content-subtle">Card</th>
              <th className="text-right px-3 py-2 font-semibold text-content-subtle w-24">Current</th>
              <th className="text-right px-3 py-2 font-semibold text-content-subtle w-32">New Value</th>
            </tr>
          </thead>
          <tbody>
            {dataCards.map((card, index) => {
              const cardErrors = validationErrors[card.id] || [];
              const hasError = cardErrors.length > 0;
              const enteredValue = values[card.id] || '';
              const hasNewValue = enteredValue !== '';
              const cadence = card.updateCadence ?? 'monthly';
              const editable = cadence === requiredCadence;
              const neededGrain = grainForCadence(cadence);

              return (
                <tr
                  key={card.id}
                  className={`border-b border-border-default last:border-b-0 ${
                    !editable
                      ? 'bg-surface-subtle/60 text-content-subtle'
                      : hasError
                        ? 'bg-red-50/50'
                        : hasNewValue
                          ? 'bg-amber-50/50'
                          : 'bg-white'
                  } ${focusedRowIndex === index ? 'ring-1 ring-inset ring-primary-500/30' : ''}`}
                >
                  {/* Card info column - read only */}
                  <td className="px-3 py-2">
                    {card.path ? (
                      <>
                        <p className="text-xs text-content-subtlest truncate">{card.path}</p>
                        <p className="text-sm font-medium text-content-default truncate">{card.name}</p>
                      </>
                    ) : (
                      <p className="text-sm font-medium text-content-default truncate">{card.name}</p>
                    )}
                    <p className="text-[11px] text-content-subtlest mt-0.5">
                      {CADENCE_LABEL[cadence]}
                      {!editable && ` — switch to ${GRAIN_LABEL[neededGrain]} to enter data`}
                    </p>
                    {hasError && (
                      <p className="text-xs text-red-600 flex items-center gap-1 mt-0.5">
                        <WarningIcon size={12} />
                        {cardErrors[0].message}
                      </p>
                    )}
                  </td>

                  {/* Current value column - read only */}
                  <td className="px-3 py-2 text-right text-content-subtle">
                    {card.nodeType === 'status_card' ? (
                      card.currentValue === 1 ? (
                        <span>Yes</span>
                      ) : card.currentValue === 0 ? (
                        <span>No</span>
                      ) : (
                        <span className="text-content-subtlest italic">—</span>
                      )
                    ) : card.currentValue !== null ? (
                      <span>
                        {card.currentValue}
                        {getValueSuffix(card.dataValueType)}
                      </span>
                    ) : (
                      <span className="text-content-subtlest italic">—</span>
                    )}
                  </td>

                  {/* New value column - editable only when cadence matches the period grain */}
                  <td className="px-1 py-1">
                    {!editable ? (
                      <p className="text-xs text-content-subtlest text-right px-2 italic">Locked</p>
                    ) : card.nodeType === 'status_card' ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant={enteredValue === '1' ? 'primary' : 'outline'}
                          size="sm"
                          onClick={() => {
                            const newValues = { ...values };
                            newValues[card.id] = enteredValue === '1' ? '' : '1';
                            onValuesChange(newValues);
                            const newErrors = { ...validationErrors };
                            delete newErrors[card.id];
                            onValidationErrorsChange(newErrors);
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          variant={enteredValue === '0' ? 'primary' : 'outline'}
                          size="sm"
                          onClick={() => {
                            const newValues = { ...values };
                            newValues[card.id] = enteredValue === '0' ? '' : '0';
                            onValuesChange(newValues);
                            const newErrors = { ...validationErrors };
                            delete newErrors[card.id];
                            onValidationErrorsChange(newErrors);
                          }}
                        >
                          No
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <input
                          ref={(el) => {
                            if (el) {
                              inputRefs.current.set(card.id, el);
                            } else {
                              inputRefs.current.delete(card.id);
                            }
                          }}
                          type="text"
                          inputMode="decimal"
                          value={enteredValue}
                          onChange={(e) =>
                            handleValueChange(card.id, e.target.value, card.dataValueType, card.levelCalculationMode)
                          }
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          onPaste={(e) => handlePaste(e, index)}
                          onFocus={() => setFocusedRowIndex(index)}
                          onBlur={() => setFocusedRowIndex(null)}
                          placeholder={
                            card.dataValueType === 'percentage' && !card.levelCalculationMode?.startsWith('deviation')
                              ? '0-100'
                              : 'Enter'
                          }
                          className={`w-full text-right px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            hasError
                              ? 'border border-red-300 bg-red-50 text-red-900'
                              : 'border border-border-default bg-white hover:border-border-hover'
                          }`}
                          aria-invalid={hasError}
                        />
                        <span className="text-xs text-content-subtle w-4 text-left">
                          {getValueSuffix(card.dataValueType)}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
